import { Injectable, Injector, NgZone } from '@angular/core';
import { HttpHeaders, HttpResponse } from '@angular/common/http';
import { Record } from "@common/data-models/records/record";
import { ServiceRecord } from '@common/data-models/service/service-record';
import { ServiceMoveRecord } from '@common/data-models/service/service-move-record';
import { ServiceResponse } from "@common/data-models/service/service-response";
import { ObjectTypes } from "@common/enums/object-types";
import { DataStore } from "@common/data-models/data-store/data-store";
import { Util } from "@common/utils/util";
import { RecordFactory } from "@common/data-models/records/record-factory";
import { ObjectProperties } from "@common/data-models/service/object-properties";
import { NomWebServicesJson } from "@common/services/nom-web-services-json";
import { Loader } from '@loader/loader';
import { LinkObject } from '../data-models/service/link-object';
import { ApiServiceRecord } from '../data-models/service/api-service-record';
import { IBasicContentService } from '@common/interfaces/i-basic-content-service';
import { AppService } from '@suite/app.service';

@Injectable({ providedIn: 'root' })
export class BasicContentService implements IBasicContentService {
    private appService: AppService;
    constructor(
        public nomWebServicesJson: NomWebServicesJson,
        private ngZone: NgZone,
        injector: Injector
    ) {
        setTimeout(() => this.appService = injector.get(AppService));
    }

    public someText = "someText";

    public async getBasicContent<T extends Record>(database: string, path: string, guid: string, hitHighlightTerm: string, referal: string, contentType: string): Promise<ServiceResponse<T>> { // get
        let response: HttpResponse<any> = null;
        try {
            response = await this.nomWebServicesJson.get(
                `/api/${contentType}`, {
                headers: new HttpHeaders({
                    "Content-Type": "application/json",
                    "X-ENPS-TOKEN": this.nomWebServicesJson.NomTokenId
                }),
                responseType: 'json',
                params: {
                    database: database,
                    path: path,
                    guid: guid,
                    hitHighlightTerm: hitHighlightTerm
                },
                observe: 'response'
            });
        } catch (error) {
            this.nomWebServicesJson.showErrorModal({
                message: "Unable to get basic content",
                detailMessage: `Unable to get basic content. \n\rStackTrace: NomWebServicesJson.getBasicContent(); \n\rError: ${error}`
            });
            return new ServiceResponse<T>(false, "Unable to get basic content " + error.toString());
        }
        let rec: ServiceRecord = Object.assign(new ServiceRecord, response.body);
        rec.instantiateChildren();
        let converted = this.nomWebServicesJson.Convertor.fromServiceRecord(rec);

        await DataStore.set(converted);
        return new ServiceResponse<T>(true, DataStore.byGuid<T>(converted.guid, referal));
    }

    public async deleteBasicContent(database: string, path: string, guid: string, type: ObjectTypes, referal: string): Promise<ServiceResponse<Record>> { // delete
        let deleteBody = {
            database: database,
            path: path,
            guid: guid,
            type: type,
            fieldsToUpdate: []
        }
        let response: HttpResponse<any> = null;
        try {
            response = await this.nomWebServicesJson.delete(
                "/api/BasicContent", {
                headers: new HttpHeaders({
                    "Content-Type": "application/json",
                    "X-ENPS-TOKEN": this.nomWebServicesJson.NomTokenId
                }),
                responseType: 'json',
                body: JSON.stringify(deleteBody),
                observe: 'response'
            });
        } catch (e) {
            let msg: string = "Unable to delete content";
            let isLinkViolationError: boolean = false;
            if (e && e.error && e.error.indexOf("CAST_LINK_VIOLATION") != -1) {
                isLinkViolationError = true;
            }
            if (!isLinkViolationError) {
                this.nomWebServicesJson.showErrorModal({
                    message: msg,
                    detailMessage: `Unable to delete content. \n\rStackTrace: NomWebServicesJson.deleteBasicContent(); \n\rError: ${e.error}`
                });
            } else {
                e.error = "[CAST_LINK_VIOLATION] " + e.error;
            }
            return new ServiceResponse<Record>(false, "Unable to delete basic content " + e.error);
        }
        let rec: ServiceMoveRecord = Object.assign(new ServiceMoveRecord, response.body);

        let dataStoreRecord = DataStore.byGuid<Record>(guid, referal);
        if (rec.NewPath && dataStoreRecord) dataStoreRecord.path.fullPath = rec.NewPath;
        return new ServiceResponse<Record>(true, dataStoreRecord);
    }

    public async updateBasicContent<T extends Record>(database: string, path: string, guid: string, keepApproved: boolean, fields: { [id: string]: any }, updateMessage: boolean, referal: string): Promise<ServiceResponse<T>> {
        let requestParams = {};
        let response: HttpResponse<any> = null;
        try {
            //loader.show();
            Util.escapeSpecialCharsForFileName(fields);
            let fieldsToUpdate = this.nomWebServicesJson.Convertor.toENPSFields(fields);
            if (!updateMessage) {
                // always need to adjust mod time, let WS handle expires
                fieldsToUpdate.push({
                    FieldName: "ModTime",
                    FieldValue: new Date().toJSON()
                });
            }
            let type: number = (fields.type || fields.Type);
            if (!type) {
                // throw error
                this.nomWebServicesJson.showErrorModal({
                    message: "Unable to update content",
                    detailMessage: "Unable to update content. No type specified for object with path: " + path + " and guid " + guid
                });
                return new ServiceResponse<T>(false, "Unable to update basic content: no type specified");
            }
            requestParams = {
                database: database,
                path: path,
                guid: guid,
                type: type,
                keepApproved: keepApproved,
                fieldsToUpdate: fieldsToUpdate
            };
            response = await this.nomWebServicesJson.patch(
                "/api/BasicContent", 
                requestParams, {
                headers: new HttpHeaders({
                    "Content-Type": "application/json",
                    "X-ENPS-TOKEN": this.nomWebServicesJson.NomTokenId
                }),
                responseType: 'json',
                observe: 'response'
            });
        } catch (error) {
            this.nomWebServicesJson.showErrorModal({
                message: "Unable to update content",
                detailMessage: `Unable to update content. \n\rStackTrace: NomWebServicesJson.updateBasicContent(); \n\rError: ${error}`
            });
            return new ServiceResponse<T>(false, "Unable to update basic content " + error.toString());
        }
        let objectProperties = new ObjectProperties(response.body);
        let record = DataStore.byGuid<T>(guid, referal);
        this.nomWebServicesJson.Convertor.deserialize(record, objectProperties);

        return new ServiceResponse<T>(true, record);
    }

    public async createBasicContent<T extends Record>(database: string, path: string, keepApproved: boolean, fields: { [id: string]: any }, referal: string): Promise<ServiceResponse<T>> {
        let requestParams = {};
        let response: HttpResponse<any> = null;
        try {
            Util.escapeSpecialCharsForFileName(fields);
            let fieldsToUpdate = this.nomWebServicesJson.Convertor.toENPSFields(fields);
            // always need to include create time, let WS handle expires
            fieldsToUpdate.push({
                FieldName: "CreateTime",
                FieldValue: new Date().toJSON()
            });
            requestParams = {
                database: database,
                path: path,
                keepApproved: keepApproved,
                fieldsToUpdate: fieldsToUpdate
            }
            response = await this.nomWebServicesJson.put(
                "/api/BasicContent", 
                requestParams, {
                headers: new HttpHeaders({
                    "Content-Type": "application/json",
                    "X-ENPS-TOKEN": this.nomWebServicesJson.NomTokenId
                }),
                responseType: 'json',
                observe: 'response'
            });
        } catch (error) {
            this.nomWebServicesJson.showErrorModal({
                message: "Unable to create content",
                detailMessage: `Unable to create content!. \n\rStackTrace: NomWebServicesJson.createBasicContent(); \n\rError: ${error}`
            });
            return new ServiceResponse<T>(false, "Unable to create basic content " + error.toString());
        }
        let objectProperties = new ObjectProperties(response.body);
        let record = null;
        if (fields.Type == ObjectTypes.PSTVIEWSTATE) {
            record = RecordFactory.createViewStateForType(fields.PSTViewStateType);
        } else {
            record = RecordFactory.createRecordForType(fields.Type);
        }

        this.nomWebServicesJson.Convertor.deserialize(record, objectProperties);
        await DataStore.set(record);
        return new ServiceResponse<T>(true, DataStore.byGuid<T>(record.guid, referal));
    }


    public async getRecord<T extends Record>(database: string, path: string, guid: string, referal: string): Promise<ServiceResponse<T>> {
        let response: HttpResponse<any> = null;
        try {
            response = await this.nomWebServicesJson.get(
                "/api/BasicContent",
                {
                    headers: new HttpHeaders({
                        "Content-Type": "application/json",
                        "X-ENPS-TOKEN": this.nomWebServicesJson.NomTokenId
                    }),
                    responseType: 'json',
                    params: {
                        database: database,
                        path: path,
                        guid: guid,
                        hitHighlightTerm: "",
                        returnText: "true"
                    },
                    observe: 'response'
                });
        } catch (error) {
            this.nomWebServicesJson.showErrorModal({
                message: "Failed to load rundown content!",
                detailMessage: `Failed to load rundown content! \n\rStackTrace: NomWebServicesJson.getRecord(); \n\rError: ${error}`
            });
            return new ServiceResponse<T>(false, null);
        }
        if (this.nomWebServicesJson.platformConvertor.useFor["getRecord"]) {
            let serviceRecord = ApiServiceRecord.instance(response.body);
            let converted = this.nomWebServicesJson.platformConvertor.fromServiceRecord(serviceRecord);

            await DataStore.set(converted);
            return new ServiceResponse<T>(true, DataStore.byGuid<T>(converted.guid, referal));
        } else {
            let serviceRecord = ServiceRecord.instance(response.body);
            let converted = this.nomWebServicesJson.Convertor.fromServiceRecord(serviceRecord);

            await DataStore.set(converted);
            return new ServiceResponse<T>(true, DataStore.byGuid<T>(converted.guid, referal));
        }
    }

    public async addLink<T extends boolean>(database: string, links: Array<LinkObject>): Promise<ServiceResponse<T>> {
        try {
            const response: HttpResponse<any> = await this.nomWebServicesJson.post(
                "/api/Link", 
                { 
                    ENPSDatabase: database, 
                    Relations: links 
                }, {
                headers: new HttpHeaders({
                    "Content-Type": "application/json",
                    "X-ENPS-TOKEN": this.nomWebServicesJson.NomTokenId
                }),
                responseType: 'json',
                observe: 'response'
            });
            if (response.ok) {
                return new ServiceResponse<T>(true);
            } else {
                throw "Failed"
            }
        } catch (error) {
            this.nomWebServicesJson.showErrorModal({
                message: "Unable to create content",
                detailMessage: `Unable to create content!. \n\rStackTrace: NomWebServicesJson.addLink(); \n\rError: ${error}`
            });
            return new ServiceResponse<T>(false, "Unable to create link " + error.toString());
        }
    }
}
